import styled from '@emotion/styled';
import type { FC, MouseEvent, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import typography from 'storybookConfig/mixins/typography';
import { breakpointValues } from 'storybookConfig/stories/theme';

/**
 * Props
 */
export type Size = 'sm' | 'md' | 'lg' | 'xl';

interface ModalProps {
  children: ReactNode;
  animation?: boolean; // default true
  centered?: boolean; // vertically center the modal; default false
  fullScreen?: boolean; // full screen modal; default false
  onHide?: () => void;
  onShow?: () => void;
  show?: boolean; // controlled show/hide
  closeOnBackdropClick?: boolean; // close when the backdrop is clicked; default true
  trapFocus?: boolean; // trap keyboard focus inside the modal while open; default false
  size?: Size; // default 'md'
  // Additional props used for testing and accessibility
  'aria-labelledby'?: string;
  'aria-modal'?: boolean | 'true' | 'false';
  role?: string;
  'data-testid'?: string;
  id?: string;
}

interface HeaderProps {
  children: ReactNode;
  closeButton?: boolean; // default false
}

/**
 * Context
 */
type ContextType = {
  handleClose: () => void;
};
const ModalContext = createContext<ContextType | null>(null);

const useModalContext = () => {
  const context = useContext(ModalContext);

  if (context == null) {
    throw new Error('Modal components must be wrapped in <Modal.Root />');
  }

  return context;
};

/**
 * Focus trap (only used when `trapFocus` is on)
 */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Every Modal.Root dialog wrapper carries this attribute
const MODAL_ROOT_SELECTOR = '[data-modal-root]';

// Focus inside one of these, outside the trapped modal and not containing it, belongs to another
// dialog stacked above it or to content the modal renders elsewhere (mark such content with
// `data-modal-owner`), so the trap leaves it alone. Inside the trapped content only a nested
// Modal.Root counts, so an inline element with one of these attributes is not an exit.
const FOREIGN_FOCUS_SCOPE_SELECTOR =
  '[data-modal-root], [role="dialog"], [role="alertdialog"], [aria-modal="true"], [data-modal-owner]';

// How long to wait for the open animation before focusing anyway (the transition is 0.3s)
const OPEN_FOCUS_TIMEOUT_MS = 500;

const isRenderedVisible = (element: HTMLElement) => {
  const visibility = getComputedStyle(element).getPropertyValue('visibility');
  return visibility !== 'hidden' && visibility !== 'collapse';
};

// Whether a candidate is out of the Tab order: hidden by attribute or style, or under an
// `aria-hidden`/`inert` ancestor. `hasLayout` is false where nothing has layout (jsdom), which
// skips the offsetParent check that would otherwise treat every element as unrendered.
const isUnfocusable = (element: HTMLElement, container: HTMLElement, hasLayout: boolean) => {
  if (element.getAttribute('tabindex') === '-1') return true;
  // visibility inherits, so the element's own value covers hidden ancestors and visible overrides
  if (!isRenderedVisible(element)) return true;
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    if (
      node.hidden ||
      node.hasAttribute('inert') ||
      node.getAttribute('aria-hidden') === 'true' ||
      getComputedStyle(node).getPropertyValue('display') === 'none'
    ) {
      return true;
    }
    if (node === container) break;
  }
  // offsetParent is null for elements that are not rendered, and for position fixed ones
  return (
    hasLayout &&
    element.offsetParent === null &&
    getComputedStyle(element).getPropertyValue('position') !== 'fixed'
  );
};

interface FocusTrap {
  container: HTMLElement;
  restoreFocusTo: HTMLElement | null;
  focusInitial: () => void;
}

// Open trapped modals, oldest first. Only the last (top-most) one handles Tab.
const focusTrapStack: FocusTrap[] = [];

/**
 * Components
 */
const StyledBackdrop = styled.div<{ animation: boolean; isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100vh;
  opacity: ${({ isOpen }) => (isOpen ? 0.6 : 0)};
  background: ${({ theme }) => theme.color.bodyTextSecondary};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: ${({ animation }) =>
    animation ? 'visibility 0.3s ease-in-out,opacity 0.3s ease-in-out' : 'none'};
`;

const StyledDialog = styled.div<{ animation: boolean; centered: boolean; isOpen: boolean }>`
  position: fixed;
  top: ${({ centered }) => (centered ? 0 : '32px')};
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: ${({ centered }) => (centered ? 'center' : 'flex-start')};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  z-index: 1041; // 1 higher than the Backdrop
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transition: ${({ animation }) =>
    animation ? 'visibility 0.3s ease-in-out,opacity 0.3s ease-in-out' : 'none'};

  @media screen and (max-width: ${breakpointValues.sm - 1}px) {
    top: 0;
    align-items: flex-start;
  }
`;

const StyledModal = styled.div<{ size: Size; fullScreen: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  border-radius: 8px;
  overflow-x: hidden;
  overflow-y: auto;
  height: ${({ fullScreen }) => (fullScreen ? '100vh' : 'auto')};
  max-height: ${({ fullScreen }) => (fullScreen ? '100vh' : '80vh')};
  max-width: ${({ size, fullScreen }) => {
    if (fullScreen) return 'none';

    switch (size) {
      case 'sm':
        return '400px';
      case 'lg':
        return '800px';
      case 'xl':
        return '1200px';
      default: {
        return '600px';
      }
    }
  }};
  background: ${({ theme }) => theme.color.white};

  // Only a trapped modal has tabindex -1. It takes focus as a fallback, so it shows no ring.
  &[tabindex='-1'] {
    outline: none;
  }

  @media screen and (max-width: ${breakpointValues.sm - 1}px) {
    height: 100dvh;
    max-height: 100dvh;
    max-width: none;
    width: 100%;
    border-radius: 0;
  }
`;

const Root: FC<ModalProps> = ({
  animation = true,
  centered = false,
  onHide,
  onShow,
  show,
  size = 'md',
  fullScreen = false,
  closeOnBackdropClick = true,
  trapFocus = false,
  children,
  ...rest // pass through any other props, like `data-testid` and `aria-labelledby`
}) => {
  const [uncontrolledShow, setUncontrolledShow] = useState<boolean>(true);
  const isOpen: boolean = useMemo(() => show ?? uncontrolledShow, [show, uncontrolledShow]);
  const handleClose = useCallback(() => {
    setUncontrolledShow(false);
    // Call onHide if it exists
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onHide && onHide();
  }, [onHide]);
  const contextValue: ContextType = useMemo(() => ({ handleClose }), [handleClose]);

  // Call onShow when the modal is opened
  useEffect(() => {
    if (onShow && isOpen) {
      onShow();
    }
  }, [isOpen, onShow]);

  // Handle off-modal clicks
  const hovered = useRef(false);
  const onHoverStart = () => {
    hovered.current = true;
  };
  const onHoverEnd = () => {
    hovered.current = false;
  };
  const modalRef = useRef<HTMLDivElement>(null);
  const handleCloseOnOutsideClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!closeOnBackdropClick) return;
    // A trapped modal ignores the hover flag: it closes only for clicks on this full-viewport
    // wrapper itself. So keyboard focus inside does not swallow a backdrop click, and neither a
    // button activated with Enter or Space nor a click in portaled content (both bubble here
    // through React) closes it.
    if (trapFocus) {
      if (event.target === event.currentTarget) handleClose();
      return;
    }
    if (!hovered.current) handleClose();
  };

  // Focus trap: focus the first focusable (or the container) on open, keep Tab inside while
  // open and top-most, restore focus on close
  useLayoutEffect(() => {
    const container = modalRef.current;
    if (!trapFocus || !isOpen || !container) return undefined;

    const getFocusable = () => {
      const hasLayout = container.getClientRects().length > 0;
      return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => !isUnfocusable(element, container, hasLayout)
      );
    };
    const focusInitial = () => (getFocusable()[0] ?? container).focus();
    const hasFocus = () => container.contains(document.activeElement);
    const isInForeignScope = (active: Element) => {
      // A Modal.Root rendered inside this modal's content is open above it
      const nestedModal = active.closest(MODAL_ROOT_SELECTOR);
      if (nestedModal && container.contains(nestedModal)) return true;
      if (container.contains(active)) return false;
      const scope = active.closest(FOREIGN_FOCUS_SCOPE_SELECTOR);
      return !!scope && !scope.contains(container);
    };

    const trap: FocusTrap = {
      container,
      restoreFocusTo: document.activeElement as HTMLElement | null,
      focusInitial,
    };
    focusTrapStack.push(trap);

    // During the open animation the dialog can still be visibility:hidden, so nothing in it can
    // take focus yet. Retry each frame until it is visible (or the timeout passes), unless focus
    // already got inside or another trapped modal opened on top.
    let frame: number | undefined;
    let firstFrameAt: number | undefined;
    const retryInitialFocus = (time: number) => {
      frame = undefined;
      if (focusTrapStack[focusTrapStack.length - 1] !== trap || hasFocus()) return;
      if (firstFrameAt === undefined) firstFrameAt = time;
      const timedOut = time - firstFrameAt >= OPEN_FOCUS_TIMEOUT_MS;
      if (timedOut || isRenderedVisible(container)) focusInitial();
      if (!timedOut && !hasFocus()) frame = requestAnimationFrame(retryInitialFocus);
    };
    if (isRenderedVisible(container)) focusInitial();
    if (!hasFocus()) frame = requestAnimationFrame(retryInitialFocus);

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Tab' || focusTrapStack[focusTrapStack.length - 1] !== trap) return;
      const active = document.activeElement;
      const inside = !!active && active !== container && container.contains(active);
      if (active && isInForeignScope(active)) return;
      const focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (!inside || active === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (!inside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown);
      const index = focusTrapStack.indexOf(trap);
      if (index === -1) return;
      focusTrapStack.splice(index, 1);
      if (index < focusTrapStack.length) {
        // Closing under another trapped modal: leave focus there, and hand it our restore target
        // if its own target was inside this modal
        const above = focusTrapStack[index];
        if (!above.restoreFocusTo || container.contains(above.restoreFocusTo)) {
          above.restoreFocusTo = trap.restoreFocusTo;
        }
        return;
      }
      const target = trap.restoreFocusTo;
      const resumed = focusTrapStack[focusTrapStack.length - 1];
      if (!resumed) {
        target?.focus?.();
      } else if (target?.isConnected && resumed.container.contains(target)) {
        target.focus();
      } else {
        resumed.focusInitial();
      }
    };
  }, [trapFocus, isOpen]);

  return (
    <ModalContext.Provider value={contextValue}>
      <StyledBackdrop animation={animation} isOpen={isOpen} />
      <StyledDialog
        animation={animation}
        centered={centered}
        isOpen={isOpen}
        onClick={handleCloseOnOutsideClick}
        data-testid="modal-dialog"
        data-modal-root=""
      >
        {isOpen && (
          <StyledModal
            ref={modalRef}
            tabIndex={trapFocus ? -1 : undefined}
            fullScreen={fullScreen}
            size={size}
            onFocus={onHoverStart}
            onMouseEnter={onHoverStart}
            onMouseLeave={onHoverEnd}
            {...rest}
          >
            {children}
          </StyledModal>
        )}
      </StyledDialog>
    </ModalContext.Provider>
  );
};

/**
 * Header
 */
const StyledHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray200};
`;

const StyledCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.bodyTextSecondary};
`;

const Header: FC<HeaderProps> = ({ closeButton, children }) => {
  const { handleClose } = useModalContext();
  const CloseButton = (
    <StyledCloseButton onClick={handleClose} aria-label="Close">
      ×
    </StyledCloseButton>
  );
  return (
    <StyledHeader data-testid="modal-header">
      {children}
      {closeButton && CloseButton}
    </StyledHeader>
  );
};

/**
 * Title
 */
const StyledTitle = styled.div`
  ${typography('Headings/H3')};
  color: ${({ theme }) => theme.color.bodyTextPrimary};
  margin: 0;
`;

const Title: FC<{ children: ReactNode }> = ({ children }) => (
  <StyledTitle data-testid="modal-title">{children}</StyledTitle>
);

/**
 * Body
 */
const StyledBody = styled.div`
  padding: 24px;
  flex: 1;
  overflow-y: auto;
`;

const Body: FC<{ children: ReactNode }> = ({ children }) => (
  <StyledBody data-testid="modal-body">{children}</StyledBody>
);

/**
 * Footer
 */
const StyledFooter = styled.div`
  display: flex;
  padding: 0 24px 24px 24px;
  justify-content: flex-end;
  gap: 16px;

  @media screen and (max-width: ${breakpointValues.sm - 1}px) {
    flex-direction: column;
    align-items: stretch;
    padding: 16px 24px 24px;

    > * {
      justify-content: center;
      min-width: 0;
    }
  }
`;

const Footer: FC<{ children: ReactNode }> = ({ children }) => (
  <StyledFooter data-testid="modal-footer">{children}</StyledFooter>
);

export default {
  Root,
  Header,
  Title,
  Body,
  Footer,
};
